// VerifyEmailPage.jsx â€” Email verification (auto-runs if ?token=XXX in URL)

import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  Mail, CheckCircle2, XCircle, Loader2, RefreshCw, ArrowRight, Shield,
} from "lucide-react";

import ZenLogo from "../assets/images/ZEN LOGO-w 1.png";
import "../css/loginform.css";
import "../css/auth/authPages.css";

const API_URL = "https://apicrm.zenrealestate.in/api";

export default function VerifyEmailPage() {
  const navigate           = useNavigate();
  const [params]           = useSearchParams();
  const tokenFromUrl       = params.get("token");

  // status: idle | verifying | success | error
  const [status, setStatus]   = useState(tokenFromUrl ? "verifying" : "idle");
  const [message, setMessage] = useState("");
  const [email,  setEmail]    = useState(localStorage.getItem("email") || "");
  const [resending, setResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  // â”€â”€ Auto-verify if token in URL â”€â”€
  useEffect(() => {
    if (!tokenFromUrl) return;
    (async () => {
      try {
        const res = await axios.post(`${API_URL}/auth/verify-email`, { token: tokenFromUrl });
        setStatus("success");
        setMessage(res.data.message || "Email verified successfully!");
        toast.success("Email verified successfully!");
      } catch (err) {
        setStatus("error");
        setMessage(err.response?.data?.message || "Verification failed. The link may be invalid or expired.");
      }
    })();
  }, [tokenFromUrl]);

  // â”€â”€ Cooldown timer for resend â”€â”€
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setInterval(() => setResendCooldown(s => s - 1), 1000);
    return () => clearInterval(t);
  }, [resendCooldown]);

  const handleResend = async () => {
    if (!email) return toast.error("Please enter your email");
    setResending(true);
    try {
      await axios.post(`${API_URL}/auth/resend-verification`, { email: email.trim().toLowerCase() });
      toast.success("Verification email sent! Please check your inbox.");
      setResendCooldown(60);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to send verification email");
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="login-wrapper">
      {/* â”€â”€ Brand panel â”€â”€ */}
      <div className="login-left">
        <div className="login-brand">
          <img src={ZenLogo} alt="Zen Realty" className="login-brand-logo" />
          <h1>Verify Your<br/>Email Address</h1>
          <p>
            Verifying your email helps us keep your account secure and unlocks
            the full ZENREAL ESTATES CRM experience.
          </p>
        </div>

        <div className="login-features">
          <div className="login-feature-item">
            <div className="login-feature-icon"><Mail size={18} /></div>
            <span>Confirms your contact email is valid</span>
          </div>
          <div className="login-feature-item">
            <div className="login-feature-icon"><Shield size={18} /></div>
            <span>Adds a layer of account security</span>
          </div>
          <div className="login-feature-item">
            <div className="login-feature-icon"><CheckCircle2 size={18} /></div>
            <span>Receive important notifications & alerts</span>
          </div>
        </div>

        <p className="login-left-footer">
          Â© {new Date().getFullYear()} Zen Realty â€” Internal Use Only
        </p>
      </div>

      {/* â”€â”€ Form panel â”€â”€ */}
      <div className="login-right">
        <div className="login-form">

          {/* â”€â”€ Verifying â”€â”€ */}
          {status === "verifying" && (
            <div className="verify-status idle">
              <div className="verify-status-icon">
                <Loader2 size={32} className="animate-spin" style={{ animation: "spin 1s linear infinite" }} />
              </div>
              <h3>Verifying your emailâ€¦</h3>
              <p>Please wait while we confirm your verification token.</p>
            </div>
          )}

          {/* â”€â”€ Success â”€â”€ */}
          {status === "success" && (
            <>
              <div className="verify-status success">
                <div className="verify-status-icon">
                  <CheckCircle2 size={32} />
                </div>
                <h3>Email Verified Successfully!</h3>
                <p>{message}</p>
              </div>
              <Link to="/" className="signIn-btn" style={{ display: "block", textAlign: "center", textDecoration: "none" }}>
                Continue to Login
              </Link>
            </>
          )}

          {/* â”€â”€ Error â”€â”€ */}
          {status === "error" && (
            <>
              <div className="verify-status error">
                <div className="verify-status-icon">
                  <XCircle size={32} />
                </div>
                <h3>Verification Failed</h3>
                <p>{message}</p>
              </div>

              <div className="input-field">
                <label htmlFor="resendEmail">Resend verification link</label>
                <input
                  id="resendEmail"
                  type="email"
                  placeholder="you@zenrealty.co"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <button
                className="signIn-btn"
                onClick={handleResend}
                disabled={resending || resendCooldown > 0}
              >
                {resending ? "Sendingâ€¦" :
                 resendCooldown > 0 ? `Resend in ${resendCooldown}s` :
                 "Resend Verification Email"}
              </button>
              <button className="auth-btn-outline" onClick={() => navigate("/")}>
                Back to Login
              </button>
            </>
          )}

          {/* â”€â”€ Idle (no token in URL â€” show resend form) â”€â”€ */}
          {status === "idle" && (
            <>
              <div className="login-form-header">
                <h2 className="welcome-text">Verify Your Email</h2>
                <p className="subtitle">
                  Enter the email address associated with your account and we'll send
                  you a verification link.
                </p>
              </div>

              <div className="auth-banner info">
                <Mail size={16} />
                <div>
                  <strong>Already verified?</strong> Email verification is optional.
                  You can <Link to="/" style={{ color: "#1D4ED8", fontWeight: 600 }}>sign in</Link> at any time.
                </div>
              </div>

              <div className="input-field">
                <label htmlFor="resendEmail">Email Address</label>
                <input
                  id="resendEmail"
                  type="email"
                  placeholder="you@zenrealty.co"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              <button
                className="signIn-btn"
                onClick={handleResend}
                disabled={resending || resendCooldown > 0}
              >
                {resending ? "Sendingâ€¦" :
                 resendCooldown > 0 ? `Resend in ${resendCooldown}s` :
                 "Send Verification Email"}
              </button>

              <p className="footer-text">
                <Link to="/">â† Back to Login</Link>
              </p>
            </>
          )}
        </div>
      </div>

      <ToastContainer />

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
